import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { catalogQueries } from "@surgia/api/src/queryStore/admin/catalog";
import config from "@surgia/config";
import { useState } from "react";

interface UseProductAssignmentProps {
  toggledProductsApiTracker: any[];
  currentStep: number;
  setCurrentStep: (step: number | ((prev: number) => number)) => void;
  tenantId: string;
  currentPage: number;
  search: string;
  filters: any;
  resetStates: () => void;
}

export const useProductAssignment = ({
  toggledProductsApiTracker,
  currentStep,
  setCurrentStep,
  tenantId,
  currentPage,
  search,
  filters,
  resetStates,
}: UseProductAssignmentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Mutation for assigning products - using the correct mutation
  const assignProductsMutation = useMutation({
    ...catalogQueries.mutations.assignParentProductsToTenant(
      tenantId ? +tenantId : 0
    ),

    onSuccess: () => {
      toast.success(
        t("admin.productAssignment.messages.productsAssignedSuccess")
      );
      queryClient.invalidateQueries({
        queryKey: [
          "shared",
          "tenant-products",
          {
            tenant_id: tenantId,
            component_id: config.componentId,
            page: currentPage,
            size: 10,
          },
        ],
      });
    },
    onError: () => {
      toast.error(t("admin.productAssignment.messages.productsAssignedError"), {
        description: t("admin.productAssignment.messages.tryAgain"),
      });
    },
    onSettled: () => {
      // Invalidate and refetch tenant products
      queryClient.invalidateQueries({
        queryKey: [
          "shared",
          "common-products",
          {
            component_id: config.componentId,
            tenant_id: tenantId ? +tenantId : undefined,
            name: search,
            page: currentPage,
            size: 10,
            id: filters?.id ? filters.id : undefined,
            price_from: filters?.price_from ? filters.price_from : undefined,
            price_to: filters?.price_to ? filters.price_to : undefined,
            categories: filters?.category ? filters.category : undefined,
            brands: filters?.brand ? filters.brand : undefined,
          },
        ],
      });
    },
  });

  const validateProductsSelected = () => {
    if (toggledProductsApiTracker.length === 0) {
      toast.info(t("admin.productAssignment.messages.noProductsSelected"));
      return false;
    }
    return true;
  };

  const handlePreviousStep = () => {
    if (currentStep === 2) {
      // Show confirmation modal for step 1
      setShowConfirmModal(true);
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleConfirmGoBack = async () => {
    setShowConfirmModal(false);

    // Save current progress as draft
    if (toggledProductsApiTracker.length > 0) {
      try {
        await assignProductsMutation.mutateAsync({
          parentProductIds: toggledProductsApiTracker,
        });
        toast.success("Current progress saved as draft");
      } catch (error) {
        toast.error("Failed to save draft");
      }
    }

    // Reset states and go to step 1
    resetStates();
    setCurrentStep(1);
  };

  const handleCancelGoBack = () => {
    setShowConfirmModal(false);
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      const success = await handleAssignProducts();
      if (!success) {
        return; // Don't proceed to next step if assignment failed
      }
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleAssignProducts = async () => {
    if (!validateProductsSelected()) {
      return false;
    }

    try {
      await assignProductsMutation.mutateAsync({
        parentProductIds: toggledProductsApiTracker,
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSaveAsDraft = async () => {
    if (currentStep === 1) {
      const success = await handleAssignProducts();
      if (success) {
        navigate(-1);
      }
    }
  };

  return {
    // Actions
    handlePreviousStep,
    handleNextStep,
    handleAssignProducts,
    handleSaveAsDraft,
    handleConfirmGoBack,
    handleCancelGoBack,

    // State
    assignProductsMutation,
    showConfirmModal,

    // Utilities
    validateProductsSelected,
    isAssigning: assignProductsMutation.isPending,
  };
};
